import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { WebsiteContent } from '../types/websiteCms.types';
import { loadWebsiteContent } from '../services/websiteContentService';

interface WebsiteContentContextValue {
  content: WebsiteContent | null;
  loading: boolean;
}

const WebsiteContentContext = createContext<WebsiteContentContextValue>({
  content: null,
  loading: true,
});

export function WebsiteContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<WebsiteContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWebsiteContent()
      .then(setContent)
      .catch(err => console.error('[CMS] Provider load error:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <WebsiteContentContext.Provider value={{ content, loading }}>
      {children}
    </WebsiteContentContext.Provider>
  );
}

/**
 * Hook to access website CMS content.
 * Returns the merged CMS+static content, or null while loading.
 */
export function useWebsiteContent(): WebsiteContent | null {
  const { content } = useContext(WebsiteContentContext);
  return content;
}

export function useWebsiteContentLoading(): boolean {
  const { loading } = useContext(WebsiteContentContext);
  return loading;
}
